import { EmailTemplate, Recipient } from '../types';
import { v4 as uuidv4 } from 'uuid';

/**
 * Extract variable names from template content
 * Looks for patterns like {{variableName}}
 */
export const extractVariables = (content: string): string[] => {
  const variableRegex = /\{\{([^}]+)\}\}/g;
  const variables: string[] = [];
  let match;
  
  while ((match = variableRegex.exec(content)) !== null) {
    const variableName = match[1].trim();
    if (!variables.includes(variableName)) {
      variables.push(variableName);
    }
  }
  
  return variables;
};

/**
 * Replace template variables with actual values
 */
export const replaceVariables = (
  template: string,
  variables: Record<string, string>
): string => {
  let result = template;
  
  Object.entries(variables).forEach(([key, value]) => {
    const regex = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g');
    result = result.replace(regex, value || `{{${key}}}`);
  });
  
  return result;
};

/**
 * Validate that all required variables are provided
 */
export const validateVariables = (
  template: EmailTemplate,
  recipient: Recipient
): { isValid: boolean; missingVariables: string[] } => {
  const missingVariables: string[] = [];
  
  template.variables.forEach(variable => {
    if (!recipient.variables[variable] || recipient.variables[variable].trim() === '') {
      missingVariables.push(variable);
    }
  });
  
  return {
    isValid: missingVariables.length === 0,
    missingVariables
  };
};

/**
 * Generate personalized email content for a recipient
 */
export const generatePersonalizedEmail = (
  template: EmailTemplate,
  recipient: Recipient
): { subject: string; content: string; isValid: boolean; errors: string[] } => {
  const validation = validateVariables(template, recipient);
  
  if (!validation.isValid) {
    return {
      subject: template.subject,
      content: template.content,
      isValid: false,
      errors: [`Missing variables for ${recipient.email}: ${validation.missingVariables.join(', ')}`]
    };
  }
  
  const personalizedSubject = replaceVariables(template.subject, recipient.variables);
  const personalizedContent = replaceVariables(template.content, recipient.variables);
  
  return {
    subject: personalizedSubject,
    content: personalizedContent,
    isValid: true,
    errors: []
  };
};

/**
 * Generate tracking pixel URL
 */
export const generateTrackingPixel = (campaignId: string, email: string, trackingId: string): string => {
  // Use the same format as backend for consistency
  const protocol = 'https';
  const domain = process.env.REACT_APP_API_URL;
  const encodedEmail = encodeURIComponent(email);
  return `${protocol}://${domain}/track/open/${campaignId}/${encodedEmail}/${trackingId}`;
};

/**
 * Generate tracked link URL
 */
export const generateTrackedLink = (
  originalUrl: string,
  campaignId: string,
  email: string,
  trackingId: string
): string => {
  // Use the same format as backend for consistency
  const protocol = 'https';
  const domain = process.env.REACT_APP_API_URL;
  const encodedUrl = encodeURIComponent(originalUrl);
  const encodedEmail = encodeURIComponent(email);
  return `${protocol}://${domain}/track/click/${campaignId}/${encodedEmail}/${trackingId}?url=${encodedUrl}`;
};

/**
 * Add tracking to email content
 */
export const addTrackingToEmail = (
  content: string,
  campaignId: string,
  email: string,
  trackingId: string
): string => {
  let trackedContent = content;

  // Replace links with tracked links first
  const linkRegex = /<a\s+(?:[^>]*?\s+)?href=(["'])(.*?)\1/gi;
  trackedContent = trackedContent.replace(linkRegex, (match, _quote, url) => {
    if (url.startsWith('mailto:') || url.startsWith('#') || url.includes('/track/')) {
      return match; // Don't track mailto links, anchors, or already tracked links
    }
    const trackedUrl = generateTrackedLink(url, campaignId, email, trackingId);
    return match.replace(url, trackedUrl);
  });

  // Add tracking pixel at the end (more reliable for email clients)
  const trackingPixel = `<img src="${generateTrackingPixel(campaignId, email, trackingId)}" width="1" height="1" style="display:block!important;visibility:hidden!important;opacity:0!important;position:absolute!important;top:-9999px!important;left:-9999px!important;" alt="" border="0" />`;

  // Try to insert before closing body tag, otherwise append
  if (trackedContent.includes('</body>')) {
    trackedContent = trackedContent.replace('</body>', `${trackingPixel}</body>`);
  } else if (trackedContent.includes('</html>')) {
    trackedContent = trackedContent.replace('</html>', `${trackingPixel}</html>`);
  } else {
    trackedContent += trackingPixel;
  }

  return trackedContent;
};

/**
 * Generate unique email ID
 */
export const generateEmailId = (): string => {
  return uuidv4();
};

/**
 * Parse CSV data into recipients
 */
export const parseCSVToRecipients = (
  csvData: string[][],
  emailColumnIndex: number,
  variableMapping: Record<string, number>
): Recipient[] => {
  const recipients: Recipient[] = [];
  
  // Skip header row
  for (let i = 1; i < csvData.length; i++) {
    const row = csvData[i];
    const email = row[emailColumnIndex]?.trim();
    
    if (!email || !isValidEmail(email)) {
      continue;
    }
    
    const variables: Record<string, string> = {};
    Object.entries(variableMapping).forEach(([variableName, columnIndex]) => {
      variables[variableName] = row[columnIndex]?.trim() || '';
    });
    
    recipients.push({
      email,
      variables,
      id: generateEmailId()
    });
  }
  
  return recipients;
};

/**
 * Validate email address
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Calculate analytics metrics
 */
export const calculateAnalytics = (
  totalSent: number,
  totalOpened: number,
  totalClicked: number
) => {
  return {
    openRate: totalSent > 0 ? (totalOpened / totalSent) * 100 : 0,
    clickRate: totalSent > 0 ? (totalClicked / totalSent) * 100 : 0,
    clickThroughRate: totalOpened > 0 ? (totalClicked / totalOpened) * 100 : 0
  };
};
